import { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { User, Person, UserInfo } from "@/graphql/types"
import { useCurrentUser } from "@/graphql/hooks/useAuth"

interface AuthContextType {
  user: User | null
  isAdmin?: boolean
  person: Person | null
  userInfo: UserInfo | null
  token: string | null
  login: (token: string, userData?: User) => void
  logout: () => void
  isAuthenticated: boolean
  hasActiveStore: boolean
  loading: boolean
  refetch?: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider")
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [person, setPerson] = useState<Person | null>(null)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  // Use the UserInfo query to get complete user data
  const {
    userInfo: fetchedUserInfo,
    user: fetchedUser,
    person: fetchedPerson,
    loading: userInfoLoading,
    refetch,
    hasActiveStore,
  } = useCurrentUser()

  useEffect(() => {
    // Check session storage for existing token
    const storedToken = localStorage.getItem("authTokenMyL")

    if (storedToken) {
      setToken(storedToken)
    }
    setInitialLoading(false)
  }, [])

  // Update user data when fetched from API
  useEffect(() => {
    if (fetchedUserInfo) {
      setUserInfo(fetchedUserInfo)
      if (fetchedUserInfo.User?.Person) {
        setPerson(fetchedUserInfo.User.Person)
        // Create a proper User object from the API response
        const userObj: User = {
          id: fetchedUserInfo.User.id,
          username: fetchedUserInfo.User.Person.email,
          email: fetchedUserInfo.User.Person.email,
          active: fetchedUserInfo.User.Person.active,
          verified: fetchedUserInfo.User.Person.emailValid,
          created: fetchedUserInfo.User.Person.created,
          modified: fetchedUserInfo.User.Person.created, // Using created as fallback
          deleted: 0, // Default value
        }
        setUser(userObj)
        const checkAdmin = fetchedUserInfo?.User?.Person?.PersonProfiles?.some(
          (x) => x.profileId === 3,
        )
        setIsAdmin(checkAdmin)
      }
    }
  }, [fetchedUserInfo])

  const login = (authToken: string, userData?: User) => {
    setToken(authToken)
    localStorage.setItem("authTokenMyL", authToken)

    // Refetch user info after login
    if (refetch) {
      refetch()
    }
  }

  const logout = () => {
    setUser(null)
    setPerson(null)
    setUserInfo(null)
    setToken(null)
    localStorage.removeItem("authTokenMyL")
    window.location.href = "/"
  }

  const isAuthenticated = !!token
  const loading = initialLoading || (!!token && userInfoLoading)

  const value = {
    user,
    person,
    userInfo,
    token,
    login,
    logout,
    isAuthenticated,
    loading,
    refetch,
    isAdmin,
    hasActiveStore,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
